import React from 'react';
import useNotifications from '../../../../_lib/notifications/useNotifications';

const MAX_FILE_SIZE_MB = 100 * 1000000;
const defaultAcceptedFiles =
  'image/png, image/jpg, image/jpeg, application/pdf';

export default function useUploadZone({
  maxFiles = 3,
  maxSize = MAX_FILE_SIZE_MB,
  accept = defaultAcceptedFiles,
  multiple = false,
  docType = '',
} = {}) {
  const [isUploading, setIsUploading] = React.useState(false);
  const [percentageUploadedIndex, setpercentageUploadedIndex] = React.useState(
    {}
  );
  const [files, setFiles] = React.useState(null);

  const notifications = useNotifications();

  const percentageUploaded = React.useMemo(() => {
    // @ts-expect-error TS(2339): Property 'total' does not exist on type '{}'.
    const { total, ...uploadedPercentageOfFiles } = percentageUploadedIndex;
    if (!total || Object.keys(uploadedPercentageOfFiles).length === 0) {
      return 0;
    }

    const currentTotalUploadedPercentage = Object.values(
      uploadedPercentageOfFiles
    ).reduce((a: any, b: any) => a + b);
    // @ts-expect-error TS(18046): currentTotalUploadedPercentage' is of type 'unknown'
    return Math.round((currentTotalUploadedPercentage / total) * 100);
  }, [percentageUploadedIndex]);

  const hasPreview = React.useMemo(() => {
    return !!files;
  }, [files]);

  const onDropAccepted = React.useCallback((acceptedFiles: any) => {
    setFiles(acceptedFiles);
  }, []);

  const onDropRejected = React.useCallback((dropFiles: any) => {
    if (dropFiles && dropFiles.length > maxFiles) {
      notifications.error(`Please select no more than ${maxFiles} file(s)`);
    } else if (
      dropFiles.some((dropFile: any) => !accept.includes(dropFile.type))
    ) {
      notifications.error(`Please select only supported file types`);
    }
    // eslint-disable-next-line
  }, []);

  const onRemoveAllFiles = React.useCallback((event: any) => {
    event.stopPropagation();
    setFiles(null);
    // eslint-disable-next-line
  }, []);

  const onRemoveFile = React.useCallback(
    (fileName: any) => {
      if (files === null) {
        return;
      }
      const filteredFiles = files.filter((file: any) => {
        return file.name !== fileName;
      });
      setFiles(filteredFiles.length === 0 ? null : filteredFiles);

      // eslint-disable-next-line
    },
    [files]
  );

  const defaultOnFileUploadError = React.useCallback(
    ({ file }: any) => {
      notifications.error(`Failed to upload file ${file.name}`);
    },
    // eslint-disable-next-line
    []
  );

  const handleUploads = React.useCallback(
    ({ asyncUploadFunc, onFileUploadError, onFileUploadSuccess }: any) => {
      if (!(files && files.length > 0)) {
        return Promise.reject(
          new Error('No files were selected to be uploaded')
        );
      }

      setIsUploading(true);
      const uploadPromises = [];

      setpercentageUploadedIndex({ total: 100 * files.length });
      for (let i = 0; i < files.length; i += 1) {
        uploadPromises.push(
          asyncUploadFunc({
            file: files[i],
            axiosConfig: {
              onUploadProgress: (progressEvent: any) => {
                const localProgreess = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                setpercentageUploadedIndex((prev) => {
                  return { ...prev, [files[i].name]: localProgreess };
                });
              },
            },
            docType,
          })
            .then((res: any) => {
              if (
                onFileUploadSuccess &&
                typeof onFileUploadSuccess === 'function'
              ) {
                onFileUploadSuccess(res);
              }

              return res;
            })
            .catch((error: any) => {
              if (
                onFileUploadError &&
                typeof onFileUploadError === 'function'
              ) {
                onFileUploadError({ error, file: files[i] });
              } else {
                defaultOnFileUploadError({ error, file: files[i] });
              }
            })
            .finally(() => {
              setIsUploading(false);
            })
        );
      }

      return Promise.all(uploadPromises)
        .catch(() => 1)
        .finally(() => {
          setIsUploading(false);
          setpercentageUploadedIndex({});
        });
    },
    // eslint-disable-next-line
    [files]
  );

  return {
    files,
    setFiles,
    maxFiles,
    maxSize,
    accept,
    multiple,
    hasPreview,
    onDropAccepted,
    onDropRejected,
    onRemoveAllFiles,
    onRemoveFile,
    handleUploads,
    isUploading,
    percentageUploaded,
    percentageUploadedIndex,
    inputProps: { name: 'file' },
  };
}
